import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

export default function LandingPage() {
  const navigate = useNavigate()
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,600;1,700&family=Oswald:wght@200;300;400;500;600;700&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --ink:        #0A0A0A;
          --ink-mid:    #141414;
          --cream:      #F5EFE4;
          --cream-2:    #EDE5D8;
          --text-light: #F0E8D8;
          --text-muted: #9A8A72;
          --gold:       #C8960C;
          --gold-h:     #E8B420;
          --serif: 'Cormorant Garamond', Georgia, serif;
          --caps:  'Oswald', sans-serif;
        }

        html { scroll-behavior: smooth; }

        .lp { min-height: 100vh; background: var(--ink); color: var(--text-light); font-family: var(--serif); overflow-x: hidden; -webkit-font-smoothing: antialiased; }

        /* ── NAV ────────────────────────────────────────────── */
        .lp-nav {
          position: fixed; top: 0; left: 0; right: 0; z-index: 900;
          height: 64px;
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 60px;
          transition: background 0.2s;
        }
        .lp-nav.transparent { background: transparent; }
        .lp-nav.solid       { background: var(--ink); border-bottom: 1px solid rgba(200,150,12,0.15); }

        .nav-logo {
          font-family: var(--caps);
          font-size: 15px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--text-light);
          text-decoration: none;
          display: flex; align-items: center; gap: 14px;
        }
        .nav-logo-rule { width: 28px; height: 1px; background: var(--gold); flex-shrink: 0; }
        .nav-logo-sub {
          font-family: var(--caps);
          font-size: 10px;
          font-weight: 300;
          letter-spacing: 0.22em;
          color: var(--text-muted);
          text-transform: uppercase;
        }

        .nav-links { display: flex; gap: 40px; list-style: none; }
        .nav-links a {
          font-family: var(--caps);
          font-size: 11px;
          font-weight: 400;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--text-muted);
          text-decoration: none;
          transition: color 0.15s;
        }
        .nav-links a:hover { color: var(--text-light); }

        .nav-actions { display: flex; gap: 10px; align-items: center; }

        .btn-nav-ghost {
          font-family: var(--caps);
          font-size: 10px;
          font-weight: 400;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--text-muted);
          background: transparent;
          border: 1px solid rgba(240,232,216,0.2);
          padding: 8px 20px;
          cursor: pointer;
          transition: all 0.15s;
        }
        .btn-nav-ghost:hover { color: var(--text-light); border-color: rgba(240,232,216,0.5); }

        .btn-nav-gold {
          font-family: var(--caps);
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--ink);
          background: var(--gold);
          border: none;
          padding: 9px 22px;
          cursor: pointer;
          transition: background 0.15s;
        }
        .btn-nav-gold:hover { background: var(--gold-h); }

        /* ── HERO ────────────────────────────────────────────── */
        .lp-hero {
          position: relative;
          height: 100vh; min-height: 700px;
          display: flex; flex-direction: column;
          justify-content: flex-end;
          padding: 0 60px 80px;
          overflow: hidden;
        }

        .hero-photo {
          position: absolute; inset: 0;
          background-image: url('https://images.unsplash.com/photo-1518770660439-4636190af475?w=1800&q=80&auto=format&fit=crop');
          background-size: cover;
          background-position: center 40%;
          z-index: 0;
        }

        .hero-photo::after {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(
            180deg,
            rgba(10,8,5,0.35) 0%,
            rgba(10,8,5,0.50) 40%,
            rgba(10,8,5,0.85) 75%,
            rgba(10,8,5,0.97) 100%
          );
        }

        .hero-content { position: relative; z-index: 1; max-width: 820px; }

        .hero-kicker {
          font-family: var(--caps);
          font-size: 11px;
          font-weight: 400;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 24px;
          display: flex; align-items: center; gap: 14px;
        }
        .hero-kicker::before { content: ''; display: block; width: 32px; height: 1px; background: var(--gold); }

        .hero-h1 {
          font-family: var(--serif);
          font-size: clamp(44px, 6.5vw, 86px);
          font-weight: 300;
          font-style: italic;
          line-height: 1.0;
          letter-spacing: -0.01em;
          color: var(--text-light);
          margin-bottom: 28px;
        }
        .hero-h1 em { font-style: normal; font-weight: 700; color: var(--text-light); }

        .hero-sub {
          font-family: var(--serif);
          font-size: clamp(16px, 1.6vw, 20px);
          font-weight: 300;
          line-height: 1.65;
          color: rgba(240,232,216,0.72);
          max-width: 560px;
          margin-bottom: 44px;
        }

        .hero-actions { display: flex; gap: 14px; flex-wrap: wrap; align-items: center; }

        .btn-hero-primary {
          font-family: var(--caps);
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--ink);
          background: var(--gold);
          border: none;
          padding: 16px 36px;
          cursor: pointer;
          transition: background 0.15s;
        }
        .btn-hero-primary:hover { background: var(--gold-h); }

        .btn-hero-ghost {
          font-family: var(--caps);
          font-size: 11px;
          font-weight: 400;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--text-light);
          background: transparent;
          border: 1px solid rgba(240,232,216,0.35);
          padding: 16px 32px;
          cursor: pointer;
          transition: all 0.15s;
        }
        .btn-hero-ghost:hover { border-color: rgba(240,232,216,0.7); }

        /* ── QUOTE BREAK ─────────────────────────────────────── */
        .lp-quote {
          background: var(--ink-mid);
          border-top: 1px solid rgba(200,150,12,0.2);
          border-bottom: 1px solid rgba(200,150,12,0.2);
          padding: 72px 60px;
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 80px;
          align-items: center;
        }

        .quote-stat-block { display: flex; flex-direction: column; gap: 8px; }

        .quote-stat {
          font-family: var(--serif);
          font-size: clamp(56px, 7vw, 96px);
          font-weight: 700;
          line-height: 1;
          color: var(--gold);
          letter-spacing: -0.02em;
        }

        .quote-stat-label {
          font-family: var(--caps);
          font-size: 10px;
          font-weight: 400;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--text-muted);
        }

        .quote-rule { width: 1px; height: 100%; min-height: 80px; background: rgba(200,150,12,0.25); justify-self: center; display: none; }

        .quote-text-block { display: flex; flex-direction: column; gap: 18px; }

        .quote-pull {
          font-family: var(--serif);
          font-size: clamp(20px, 2.4vw, 30px);
          font-weight: 300;
          font-style: italic;
          line-height: 1.5;
          color: var(--text-light);
          border-left: 2px solid var(--gold);
          padding-left: 28px;
        }

        .quote-attr {
          font-family: var(--caps);
          font-size: 10px;
          font-weight: 300;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--text-muted);
          padding-left: 30px;
        }

        /* ── FEATURE SECTIONS ────────────────────────────────── */
        /* Feature 1: cream, split text+photo */
        .lp-feat-1 {
          background: var(--cream);
          padding: 96px 60px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 72px;
          align-items: center;
        }

        .feat-text-col { display: flex; flex-direction: column; gap: 0; }

        .feat-number {
          font-family: var(--caps);
          font-size: 11px;
          font-weight: 400;
          letter-spacing: 0.24em;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 16px;
        }

        .feat-h2 {
          font-family: var(--serif);
          font-size: clamp(30px, 3.5vw, 48px);
          font-weight: 700;
          line-height: 1.05;
          color: var(--ink);
          margin-bottom: 20px;
          letter-spacing: -0.01em;
        }

        .feat-body {
          font-family: var(--serif);
          font-size: 18px;
          font-weight: 300;
          line-height: 1.7;
          color: #3D2E1C;
          margin-bottom: 32px;
        }

        .feat-list { list-style: none; display: flex; flex-direction: column; gap: 12px; margin-bottom: 40px; }
        .feat-list li {
          font-family: var(--caps);
          font-size: 11px;
          font-weight: 400;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #5A3E26;
          display: flex; align-items: center; gap: 12px;
        }
        .feat-list li::before { content: ''; display: block; width: 16px; height: 1px; background: var(--gold); flex-shrink: 0; }

        .feat-photo {
          width: 100%; height: 480px;
          background-size: cover;
          background-position: center;
          overflow: hidden;
        }

        /* Feature 2: full-bleed dark photo with text overlay */
        .lp-feat-2 {
          position: relative;
          min-height: 600px;
          display: flex; flex-direction: column;
          justify-content: center;
          padding: 96px 60px;
          overflow: hidden;
        }

        .feat-2-photo {
          position: absolute; inset: 0;
          background-image: url('https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=1600&q=80&auto=format&fit=crop');
          background-size: cover;
          background-position: center 35%;
          z-index: 0;
        }
        .feat-2-photo::after {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(90deg, rgba(10,8,5,0.92) 0%, rgba(10,8,5,0.75) 50%, rgba(10,8,5,0.35) 100%);
        }

        .feat-2-content {
          position: relative; z-index: 1;
          max-width: 560px;
        }

        .feat-2-h2 {
          font-family: var(--serif);
          font-size: clamp(30px, 3.5vw, 52px);
          font-weight: 700;
          line-height: 1.05;
          color: var(--text-light);
          margin-bottom: 20px;
        }

        .feat-2-body {
          font-family: var(--serif);
          font-size: 18px;
          font-weight: 300;
          line-height: 1.7;
          color: rgba(240,232,216,0.75);
          margin-bottom: 36px;
        }

        .feat-2-items { display: flex; flex-direction: column; gap: 18px; }
        .feat-2-item {
          display: flex; gap: 16px; align-items: flex-start;
        }
        .feat-2-item-num {
          font-family: var(--caps);
          font-size: 10px;
          font-weight: 400;
          letter-spacing: 0.18em;
          color: var(--gold);
          text-transform: uppercase;
          flex-shrink: 0;
          margin-top: 3px;
        }
        .feat-2-item-text {
          font-family: var(--serif);
          font-size: 16px;
          font-weight: 300;
          line-height: 1.5;
          color: rgba(240,232,216,0.8);
        }
        .feat-2-item-text strong {
          font-weight: 600;
          color: var(--text-light);
        }

        /* Feature 3: cream-2, reverse split */
        .lp-feat-3 {
          background: var(--cream-2);
          padding: 96px 60px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 72px;
          align-items: center;
        }

        .feat-3-photo-col {
          width: 100%; height: 480px;
          background-size: cover;
          background-position: center;
          overflow: hidden;
        }

        /* ── OBJECTION SECTION ───────────────────────────────── */
        .lp-objections {
          background: var(--ink);
          padding: 96px 60px;
          border-top: 1px solid rgba(200,150,12,0.15);
        }

        .objections-header { margin-bottom: 56px; }

        .objections-label {
          font-family: var(--caps);
          font-size: 11px;
          font-weight: 400;
          letter-spacing: 0.24em;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 16px;
        }

        .objections-h2 {
          font-family: var(--serif);
          font-size: clamp(28px, 3.2vw, 46px);
          font-weight: 300;
          font-style: italic;
          line-height: 1.15;
          color: var(--text-light);
        }

        .objections-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1px;
          background: rgba(200,150,12,0.12);
          border: 1px solid rgba(200,150,12,0.12);
        }

        .objection-card {
          background: var(--ink);
          padding: 40px 36px;
        }

        .objection-q {
          font-family: var(--serif);
          font-size: 17px;
          font-weight: 600;
          line-height: 1.4;
          color: rgba(240,232,216,0.6);
          margin-bottom: 16px;
          font-style: italic;
        }

        .objection-rule { width: 24px; height: 1px; background: var(--gold); margin-bottom: 16px; }

        .objection-a {
          font-family: var(--serif);
          font-size: 16px;
          font-weight: 300;
          line-height: 1.65;
          color: rgba(240,232,216,0.8);
        }

        /* ── PRICING ────────────────────────────────────────── */
        .lp-pricing {
          background: var(--cream);
          padding: 96px 60px;
          border-top: 1px solid rgba(10,8,5,0.12);
        }

        .pricing-header { margin-bottom: 60px; }

        .pricing-label {
          font-family: var(--caps);
          font-size: 11px;
          font-weight: 400;
          letter-spacing: 0.24em;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 16px;
        }

        .pricing-h2 {
          font-family: var(--serif);
          font-size: clamp(28px, 3.2vw, 46px);
          font-weight: 700;
          line-height: 1.1;
          color: var(--ink);
          margin-bottom: 12px;
        }

        .pricing-sub {
          font-family: var(--serif);
          font-size: 17px;
          font-weight: 300;
          line-height: 1.6;
          color: #5A3E26;
          max-width: 520px;
        }

        .pricing-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0;
          border: 1px solid rgba(10,8,5,0.15);
        }

        .pricing-tier {
          padding: 48px 40px;
          border-right: 1px solid rgba(10,8,5,0.15);
          display: flex; flex-direction: column;
        }
        .pricing-tier:last-child { border-right: none; }
        .pricing-tier.featured { background: var(--ink); }

        .tier-label {
          font-family: var(--caps);
          font-size: 11px;
          font-weight: 400;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--text-muted);
          margin-bottom: 6px;
        }
        .pricing-tier.featured .tier-label { color: var(--gold); }

        .tier-name {
          font-family: var(--serif);
          font-size: 28px;
          font-weight: 700;
          color: var(--ink);
          margin-bottom: 28px;
        }
        .pricing-tier.featured .tier-name { color: var(--text-light); }

        .tier-price {
          display: flex;
          align-items: baseline;
          gap: 4px;
          margin-bottom: 6px;
        }

        .tier-dollar {
          font-family: var(--caps);
          font-size: 18px;
          font-weight: 300;
          color: #7A5A3C;
          align-self: flex-start;
          margin-top: 6px;
        }
        .pricing-tier.featured .tier-dollar { color: var(--text-muted); }

        .tier-amount {
          font-family: var(--serif);
          font-size: 64px;
          font-weight: 700;
          line-height: 1;
          color: var(--ink);
          letter-spacing: -0.03em;
        }
        .pricing-tier.featured .tier-amount { color: var(--text-light); }

        .tier-period {
          font-family: var(--caps);
          font-size: 10px;
          font-weight: 300;
          letter-spacing: 0.14em;
          color: #7A5A3C;
          margin-left: 4px;
        }
        .pricing-tier.featured .tier-period { color: var(--text-muted); }

        .tier-desc {
          font-family: var(--serif);
          font-size: 15px;
          font-weight: 300;
          line-height: 1.6;
          color: #5A3E26;
          margin-bottom: 32px;
        }
        .pricing-tier.featured .tier-desc { color: rgba(240,232,216,0.6); }

        .tier-rule { height: 1px; background: rgba(10,8,5,0.12); margin-bottom: 28px; }
        .pricing-tier.featured .tier-rule { background: rgba(200,150,12,0.2); }

        .tier-features { list-style: none; flex: 1; display: flex; flex-direction: column; gap: 12px; margin-bottom: 36px; }
        .tier-features li {
          font-family: var(--serif);
          font-size: 15px;
          font-weight: 400;
          line-height: 1.4;
          color: #3D2E1C;
          display: flex; align-items: flex-start; gap: 10px;
        }
        .pricing-tier.featured .tier-features li { color: rgba(240,232,216,0.8); }
        .tier-features li::before {
          content: '—';
          font-family: var(--caps);
          font-size: 12px;
          color: var(--gold);
          flex-shrink: 0;
          line-height: 1.4;
        }

        .tier-cta {
          font-family: var(--caps);
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          padding: 14px 24px;
          cursor: pointer;
          transition: all 0.15s;
          text-align: center;
        }
        .tier-cta-default {
          color: var(--ink);
          background: transparent;
          border: 1px solid rgba(10,8,5,0.3);
        }
        .tier-cta-default:hover { background: rgba(10,8,5,0.05); border-color: var(--ink); }

        .tier-cta-gold {
          color: var(--ink);
          background: var(--gold);
          border: none;
        }
        .tier-cta-gold:hover { background: var(--gold-h); }

        .tier-cta-ghost {
          color: rgba(240,232,216,0.7);
          background: transparent;
          border: 1px solid rgba(240,232,216,0.2);
        }
        .tier-cta-ghost:hover { border-color: rgba(240,232,216,0.5); color: var(--text-light); }

        /* ── FINAL CTA ───────────────────────────────────────── */
        .lp-cta-section {
          position: relative;
          min-height: 560px;
          display: flex; flex-direction: column;
          justify-content: center; align-items: center;
          text-align: center;
          padding: 100px 60px;
          overflow: hidden;
        }

        .cta-photo {
          position: absolute; inset: 0;
          background-image: url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1600&q=80&auto=format&fit=crop');
          background-size: cover;
          background-position: center 30%;
          z-index: 0;
        }
        .cta-photo::after {
          content: '';
          position: absolute; inset: 0;
          background: rgba(10,8,5,0.82);
        }

        .cta-content { position: relative; z-index: 1; max-width: 640px; }

        .cta-label {
          font-family: var(--caps);
          font-size: 11px;
          font-weight: 400;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 20px;
          display: flex; align-items: center; justify-content: center; gap: 14px;
        }
        .cta-label::before, .cta-label::after { content: ''; display: block; width: 24px; height: 1px; background: var(--gold); }

        .cta-h2 {
          font-family: var(--serif);
          font-size: clamp(32px, 4.5vw, 60px);
          font-weight: 300;
          font-style: italic;
          line-height: 1.1;
          color: var(--text-light);
          margin-bottom: 18px;
        }
        .cta-h2 em { font-style: normal; font-weight: 700; }

        .cta-sub {
          font-family: var(--serif);
          font-size: 18px;
          font-weight: 300;
          line-height: 1.65;
          color: rgba(240,232,216,0.65);
          margin-bottom: 44px;
        }

        .cta-actions { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; margin-bottom: 24px; }

        .btn-cta-primary {
          font-family: var(--caps);
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--ink);
          background: var(--gold);
          border: none;
          padding: 16px 40px;
          cursor: pointer;
          transition: background 0.15s;
        }
        .btn-cta-primary:hover { background: var(--gold-h); }

        .btn-cta-ghost {
          font-family: var(--caps);
          font-size: 11px;
          font-weight: 400;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(240,232,216,0.75);
          background: transparent;
          border: 1px solid rgba(240,232,216,0.3);
          padding: 16px 32px;
          cursor: pointer;
          transition: all 0.15s;
        }
        .btn-cta-ghost:hover { border-color: rgba(240,232,216,0.65); color: var(--text-light); }

        .cta-fine {
          font-family: var(--caps);
          font-size: 10px;
          font-weight: 300;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: rgba(240,232,216,0.3);
        }

        /* ── FOOTER ─────────────────────────────────────────── */
        .lp-footer {
          background: var(--ink);
          border-top: 1px solid rgba(200,150,12,0.15);
          padding: 32px 60px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }

        .footer-left {
          display: flex; align-items: center; gap: 20px;
        }

        .footer-wordmark {
          font-family: var(--caps);
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: rgba(240,232,216,0.4);
        }

        .footer-sep { width: 1px; height: 14px; background: rgba(200,150,12,0.2); }

        .footer-copy {
          font-family: var(--caps);
          font-size: 10px;
          font-weight: 300;
          letter-spacing: 0.14em;
          color: rgba(240,232,216,0.25);
          text-transform: uppercase;
        }

        .footer-links { display: flex; gap: 28px; align-items: center; }
        .footer-links a {
          font-family: var(--caps);
          font-size: 10px;
          font-weight: 300;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: rgba(240,232,216,0.35);
          text-decoration: none;
          transition: color 0.15s;
          cursor: pointer;
        }
        .footer-links a:hover { color: rgba(240,232,216,0.7); }

        /* ── RESPONSIVE ──────────────────────────────────────── */
        @media (max-width: 1024px) {
          .lp-nav { padding: 0 28px; }
          .nav-links { display: none; }
          .lp-hero { padding: 0 28px 60px; }
          .lp-quote { grid-template-columns: 1fr; gap: 40px; padding: 60px 28px; }
          .lp-feat-1, .lp-feat-3 { grid-template-columns: 1fr; gap: 40px; padding: 72px 28px; }
          .feat-photo, .feat-3-photo-col { height: 320px; }
          .lp-feat-2 { padding: 72px 28px; }
          .lp-objections { padding: 72px 28px; }
          .objections-grid { grid-template-columns: 1fr; }
          .objection-card { border-bottom: 1px solid rgba(200,150,12,0.12); }
          .lp-pricing { padding: 72px 28px; }
          .pricing-grid { grid-template-columns: 1fr; }
          .pricing-tier { border-right: none; border-bottom: 1px solid rgba(10,8,5,0.15); }
          .pricing-tier:last-child { border-bottom: none; }
          .pricing-tier.featured .tier-rule { border-color: rgba(200,150,12,0.2); }
          .lp-cta-section { padding: 80px 28px; }
          .lp-footer { padding: 28px; flex-direction: column; gap: 20px; text-align: center; }
          .footer-links { flex-wrap: wrap; justify-content: center; gap: 20px; }
        }

        @media (max-width: 640px) {
          .hero-h1 { font-size: 38px; }
          .hero-actions { flex-direction: column; align-items: flex-start; }
          .btn-hero-primary, .btn-hero-ghost { width: 100%; text-align: center; }
          .cta-actions { flex-direction: column; align-items: center; }
          .btn-cta-primary, .btn-cta-ghost { width: 100%; text-align: center; }
        }

        /* ── RISK MATRIX ─────────────────────────────────────── */
        .lp-matrix {
          background: var(--ink);
          padding: 100px 60px;
          border-top: 1px solid rgba(200,150,12,0.15);
        }

        .matrix-header {
          text-align: center;
          max-width: 560px;
          margin: 0 auto 64px;
        }

        .matrix-label {
          font-family: var(--caps);
          font-size: 11px;
          font-weight: 400;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 20px;
          display: flex; align-items: center; justify-content: center; gap: 14px;
        }
        .matrix-label::before, .matrix-label::after {
          content: ''; display: block; width: 32px; height: 1px; background: var(--gold);
        }

        .matrix-h2 {
          font-family: var(--serif);
          font-size: clamp(26px, 3vw, 44px);
          font-weight: 300;
          font-style: italic;
          line-height: 1.1;
          color: var(--text-light);
          margin-bottom: 14px;
        }
        .matrix-h2 em { font-style: normal; font-weight: 700; }

        .matrix-sub {
          font-family: var(--serif);
          font-size: 16px;
          font-weight: 300;
          line-height: 1.65;
          color: rgba(240,232,216,0.5);
        }

        .matrix-container {
          max-width: 700px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          gap: 0;
        }

        .matrix-y-axis {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          padding-right: 10px;
          align-self: stretch;
        }

        .matrix-y-text {
          font-family: var(--caps);
          font-size: 9px;
          font-weight: 400;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: rgba(240,232,216,0.3);
          writing-mode: vertical-rl;
          transform: rotate(180deg);
          white-space: nowrap;
        }

        .matrix-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .matrix-row {
          display: grid;
          grid-template-columns: 54px repeat(5, 1fr);
          gap: 3px;
          align-items: stretch;
        }

        .matrix-row-label {
          font-family: var(--caps);
          font-size: 8px;
          font-weight: 400;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: rgba(240,232,216,0.3);
          text-align: right;
          padding-right: 10px;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          line-height: 1.2;
        }

        .matrix-cell {
          aspect-ratio: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.12s, outline 0.12s;
          cursor: default;
        }
        .matrix-cell:hover {
          transform: scale(1.10);
          z-index: 2;
          outline: 1px solid rgba(200,150,12,0.6);
        }

        .matrix-cell-inner {
          font-family: var(--caps);
          font-size: 8px;
          font-weight: 500;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          line-height: 1;
        }

        .mc1 { background: rgba(245,239,228,0.05); }
        .mc1 .matrix-cell-inner { color: rgba(240,232,216,0.18); }

        .mc2 { background: rgba(200,150,12,0.20); }
        .mc2 .matrix-cell-inner { color: rgba(200,150,12,0.65); }

        .mc3 { background: rgba(200,150,12,0.52); }
        .mc3 .matrix-cell-inner { color: rgba(8,8,8,0.85); }

        .mc4 { background: var(--gold); }
        .mc4 .matrix-cell-inner { color: var(--ink); font-weight: 700; }

        .matrix-xlabels {
          display: grid;
          grid-template-columns: 54px repeat(5, 1fr);
          gap: 3px;
          margin-top: 5px;
        }

        .matrix-xlabel {
          font-family: var(--caps);
          font-size: 8px;
          font-weight: 400;
          letter-spacing: 0.09em;
          text-transform: uppercase;
          color: rgba(240,232,216,0.28);
          text-align: center;
        }

        .matrix-xaxis-label {
          font-family: var(--caps);
          font-size: 9px;
          font-weight: 400;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: rgba(240,232,216,0.28);
          text-align: center;
          margin-top: 8px;
          padding-left: 54px;
        }

        .matrix-legend {
          display: flex;
          gap: 32px;
          justify-content: center;
          margin-top: 52px;
          flex-wrap: wrap;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .legend-swatch {
          width: 14px;
          height: 14px;
          flex-shrink: 0;
        }
        .ls1 { background: rgba(245,239,228,0.05); border: 1px solid rgba(240,232,216,0.15); }
        .ls2 { background: rgba(200,150,12,0.20); }
        .ls3 { background: rgba(200,150,12,0.52); }
        .ls4 { background: var(--gold); }

        .legend-text {
          font-family: var(--caps);
          font-size: 10px;
          font-weight: 400;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: rgba(240,232,216,0.4);
        }

        @media (max-width: 1024px) {
          .lp-matrix { padding: 72px 28px; }
        }
      `}</style>

      <div className="lp">

        {/* ── NAV ─────────────────────────────────────────────── */}
        <nav className={`lp-nav ${scrolled ? 'solid' : 'transparent'}`}>
          <a className="nav-logo" href="/">
            <span>RCA Tool</span>
            <span className="nav-logo-rule" />
            <span className="nav-logo-sub">Project Governance Classification</span>
          </a>
          <ul className="nav-links">
            <li><a href="#assessment">The Assessment</a></li>
            <li><a href="#intelligence">The Intelligence</a></li>
            <li><a href="#pricing">Pricing</a></li>
          </ul>
          <div className="nav-actions">
            <button className="btn-nav-ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="btn-nav-gold" onClick={() => navigate('/assess')}>Start Assessment</button>
          </div>
        </nav>

        {/* ── HERO ─────────────────────────────────────────────── */}
        <section className="lp-hero">
          <div className="hero-photo" />
          <div className="hero-content">
            <div className="hero-kicker">Project Governance Classification</div>
            <h1 className="hero-h1">
              Every project needs<br />
              a governance level.<br />
              Most organizations <em>guess.</em>
            </h1>
            <p className="hero-sub">
              The RCA Tool scores every project's risk and complexity before work begins — and tells you exactly what oversight, documentation, and controls the category requires.
            </p>
            <div className="hero-actions">
              <button className="btn-hero-primary" onClick={() => navigate('/assess')}>
                Start Your First Assessment
              </button>
              <button className="btn-hero-ghost" onClick={() => navigate('/login')}>
                View Dashboard
              </button>
            </div>
          </div>
        </section>

        {/* ── QUOTE BREAK ─────────────────────────────────────── */}
        <div className="lp-quote">
          <div className="quote-stat-block">
            <div className="quote-stat">4</div>
            <div className="quote-stat-label">governance categories — Category 1 through 4 — each with distinct oversight, documentation, and control requirements</div>
          </div>
          <div className="quote-text-block">
            <p className="quote-pull">
              "Projects that skip the complexity assessment are three times more likely to require an emergency governance escalation mid-execution. The classification takes 45 minutes. The recovery takes months."
            </p>
            <div className="quote-attr">The pattern every PMO director recognizes — and every post-project audit eventually exposes</div>
          </div>
        </div>

        {/* ── RISK MATRIX ──────────────────────────────────────── */}
        <section className="lp-matrix">
          <div className="matrix-header">
            <div className="matrix-label">Risk Matrix</div>
            <h2 className="matrix-h2">The assessment places every project <em>here.</em></h2>
            <p className="matrix-sub">
              Likelihood versus impact. Four governance categories. One defensible classification — produced in 45 minutes before a single dollar is committed.
            </p>
          </div>
          <div className="matrix-container">
            <div className="matrix-y-axis">
              <span className="matrix-y-text">Likelihood</span>
            </div>
            <div className="matrix-main">
              {([
                { label: 'V. High', l: 5 },
                { label: 'High',    l: 4 },
                { label: 'Medium',  l: 3 },
                { label: 'Low',     l: 2 },
                { label: 'V. Low',  l: 1 },
              ] as { label: string; l: number }[]).map(({ label, l }) => (
                <div key={label} className="matrix-row">
                  <div className="matrix-row-label">{label}</div>
                  {[1, 2, 3, 4, 5].map(i => {
                    const score = l * i
                    const cls = score >= 20 ? 'mc4' : score >= 10 ? 'mc3' : score >= 5 ? 'mc2' : 'mc1'
                    const num = score >= 20 ? '4' : score >= 10 ? '3' : score >= 5 ? '2' : '1'
                    return (
                      <div key={i} className={`matrix-cell ${cls}`}>
                        <span className="matrix-cell-inner">Cat {num}</span>
                      </div>
                    )
                  })}
                </div>
              ))}
              <div className="matrix-xlabels">
                <div className="matrix-xlabel" />
                {['V. Low', 'Low', 'Medium', 'High', 'Critical'].map(lbl => (
                  <div key={lbl} className="matrix-xlabel">{lbl}</div>
                ))}
              </div>
              <div className="matrix-xaxis-label">Impact</div>
            </div>
          </div>
          <div className="matrix-legend">
            {([
              { cls: 'ls1', label: 'Cat 1 — Routine' },
              { cls: 'ls2', label: 'Cat 2 — Standard Oversight' },
              { cls: 'ls3', label: 'Cat 3 — Complex' },
              { cls: 'ls4', label: 'Cat 4 — Critical / IV&V Required' },
            ] as { cls: string; label: string }[]).map(({ cls, label }) => (
              <div key={cls} className="legend-item">
                <div className={`legend-swatch ${cls}`} />
                <span className="legend-text">{label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── FEATURE 1: THE ASSESSMENT ──────────────────────────── */}
        <section id="assessment" className="lp-feat-1">
          <div className="feat-text-col">
            <div className="feat-number">01 — The Assessment</div>
            <h2 className="feat-h2">40 questions that prevent 40 weeks of unplanned oversight.</h2>
            <p className="feat-body">
              Two dimensions. Four lifecycle phases. Twenty to forty weighted questions covering budget confidence, scope certainty, stakeholder complexity, technology newness, and organizational readiness. The output is a governance category — Category 1 through 4 — with the specific controls and artifacts each one requires.
            </p>
            <ul className="feat-list">
              <li>Risk and Complexity scored independently, then combined</li>
              <li>Pre-Charter, Initiation, Planning, and Event-Driven phases</li>
              <li>Weighted scoring mapped to 100–500 scale</li>
              <li>Governance report generated automatically on completion</li>
            </ul>
          </div>
          <div
            className="feat-photo"
            style={{ backgroundImage: "url('https://images.unsplash.com/photo-1569959220744-ff553533f492?w=900&q=80&auto=format&fit=crop')" }}
          />
        </section>

        {/* ── FEATURE 2: THE INTELLIGENCE — full-bleed dark ───────────── */}
        <section id="intelligence" className="lp-feat-2">
          <div className="feat-2-photo" />
          <div className="feat-2-content">
            <div className="feat-number" style={{ color: 'var(--gold)', marginBottom: '16px' }}>02 — The Intelligence</div>
            <h2 className="feat-2-h2">Upload the charter. The assessment pre-fills itself.</h2>
            <p className="feat-2-body">
              Attach your project charter, risk register, or Microsoft Project schedule. The AI extracts the answers that are already documented — flagging the questions that still need human judgment. Assessment time drops from 45 minutes to 10.
            </p>
            <div className="feat-2-items">
              <div className="feat-2-item">
                <span className="feat-2-item-num">Extract</span>
                <span className="feat-2-item-text"><strong>Document ingestion</strong> — upload a charter or risk register and the AI maps its contents to assessment questions automatically.</span>
              </div>
              <div className="feat-2-item">
                <span className="feat-2-item-num">Flag</span>
                <span className="feat-2-item-text"><strong>Gap identification</strong> — questions the AI cannot answer from the document are surfaced for human judgment, not silently skipped.</span>
              </div>
              <div className="feat-2-item">
                <span className="feat-2-item-num">Clarify</span>
                <span className="feat-2-item-text"><strong>Answer guidance</strong> — for any question, ask the AI for a plain-language explanation of what the question is measuring and how to answer it correctly.</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── FEATURE 3: THE PORTFOLIO ───────────────────────────────── */}
        <section className="lp-feat-3">
          <div
            className="feat-3-photo-col"
            style={{ backgroundImage: "url('https://images.unsplash.com/photo-1513467535987-fd81bc7d62f8?w=900&q=80&auto=format&fit=crop')" }}
          />
          <div className="feat-text-col">
            <div className="feat-number" style={{ color: '#7A5A3C' }}>03 — The Portfolio</div>
            <h2 className="feat-h2" style={{ color: 'var(--ink)' }}>See which projects need more oversight. Before they tell you.</h2>
            <p className="feat-body">
              The portfolio dashboard shows every project, its governance category, and the assessment status across lifecycle phases. Category 3 and 4 projects get flagged for IV&amp;V requirements. Trend analysis shows whether your portfolio is getting more complex — or whether your PMO is getting better at scoping projects correctly.
            </p>
            <ul className="feat-list">
              <li>All projects ranked by governance category at a glance</li>
              <li>Category 3 and 4 flags for IV&amp;V and executive reporting requirements</li>
              <li>Assessment phase completion tracked per project</li>
              <li>Portfolio trend analysis across assessment cycles</li>
            </ul>
          </div>
        </section>

        {/* ── OBJECTION SECTION ────────────────────────────────── */}
        <section className="lp-objections">
          <div className="objections-header">
            <div className="objections-label">Common Concerns</div>
            <h2 className="objections-h2">
              We already score risks in our project tracker.<br />
              <em style={{ opacity: 0.5 }}>That's a different question.</em>
            </h2>
          </div>
          <div className="objections-grid">
            {[
              {
                q: '"We already score risks in Smartsheet / Monday / Asana."',
                a: 'A risk log tells you what risks exist. A governance classification tells you what oversight framework the project requires before execution begins. These are different questions. The DMS-F-0505A framework requires both — and a defensible audit trail proving the classification was made.',
              },
              {
                q: '"We don\'t operate under Florida DMS rules."',
                a: 'The DMS-F-0505A framework is the classification methodology, not a legal requirement. Government agencies, regulated manufacturers, utilities, and construction PMOs use the same risk × complexity matrix because it produces defensible, auditable results — regardless of jurisdiction.',
              },
              {
                q: '"Our PMO already knows which projects are high-risk."',
                a: 'Knowing a project is high-risk is not the same as classifying it. Category 4 projects require IV&V engagement, executive reporting cadences, and specific artifact checklists. A governance classification creates the paper trail that protects the PMO director when a project is questioned in audit.',
              },
            ].map((obj) => (
              <div key={obj.q} className="objection-card">
                <div className="objection-q">{obj.q}</div>
                <div className="objection-rule" />
                <p className="objection-a">{obj.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── PRICING ─────────────────────────────────────────── */}
        <section id="pricing" className="lp-pricing">
          <div className="pricing-header">
            <div className="pricing-label">Pricing</div>
            <h2 className="pricing-h2">Straightforward pricing. No per-project fees.</h2>
            <p className="pricing-sub">Every plan includes the full assessment engine — weighted scoring, governance classification, and audit-ready PDF output.</p>
          </div>
          <div className="pricing-grid">

            {/* Tier: Investigator */}
            <div className="pricing-tier">
              <div className="tier-label">Investigator</div>
              <div className="tier-name">Starter</div>
              <div className="tier-price">
                <span className="tier-dollar">$</span>
                <span className="tier-amount">49</span>
                <span className="tier-period">/month</span>
              </div>
              <p className="tier-desc">For project managers beginning to formalize governance classification before projects start.</p>
              <div className="tier-rule" />
              <ul className="tier-features">
                <li>Up to 5 users</li>
                <li>20 active assessments</li>
                <li>Pre-Charter and Initiation phases</li>
                <li>Weighted scoring and Category classification</li>
                <li>PDF export — governance classification report</li>
                <li>Read-only share link for stakeholders</li>
              </ul>
              <button className="tier-cta tier-cta-default" onClick={() => navigate('/assess')}>
                Start Free Trial
              </button>
            </div>

            {/* Tier: Systems (featured) */}
            <div className="pricing-tier featured">
              <div className="tier-label">Systems</div>
              <div className="tier-name">Pro</div>
              <div className="tier-price">
                <span className="tier-dollar">$</span>
                <span className="tier-amount">99</span>
                <span className="tier-period">/month</span>
              </div>
              <p className="tier-desc">For PMO directors managing project classification across an active portfolio.</p>
              <div className="tier-rule" />
              <ul className="tier-features">
                <li>Up to 25 users</li>
                <li>Unlimited assessments</li>
                <li>All four lifecycle phases</li>
                <li>Portfolio dashboard and trend analysis</li>
                <li>AI document extraction — pre-fill from charter</li>
                <li>Category 3 and 4 IV&amp;V flag alerts</li>
                <li>SSO — Google and Microsoft</li>
                <li>Priority support</li>
              </ul>
              <button className="tier-cta tier-cta-gold" onClick={() => navigate('/assess')}>
                Start Free Trial
              </button>
            </div>

            {/* Tier: Compliance */}
            <div className="pricing-tier">
              <div className="tier-label">Compliance</div>
              <div className="tier-name">Enterprise</div>
              <div className="tier-price">
                <span className="tier-dollar">$</span>
                <span className="tier-amount">399</span>
                <span className="tier-period">/month</span>
              </div>
              <p className="tier-desc">For government agencies and regulated industries where governance classification is an audit requirement.</p>
              <div className="tier-rule" />
              <ul className="tier-features">
                <li>Unlimited users</li>
                <li>ISO 9001 / AS9100 output format</li>
                <li>Full audit trail with timestamps</li>
                <li>DMS-F-0505A compliance documentation</li>
                <li>SLA guarantee</li>
                <li>Dedicated implementation support</li>
              </ul>
              <button className="tier-cta tier-cta-default" onClick={() => navigate('/login')}>
                Contact Sales
              </button>
            </div>

          </div>
        </section>

        {/* ── FINAL CTA ────────────────────────────────────────── */}
        <section className="lp-cta-section">
          <div className="cta-photo" />
          <div className="cta-content">
            <div className="cta-label">Get Started</div>
            <h2 className="cta-h2">
              Your next project is already<br />in motion.<br />
              <em>Classify it before it classifies you.</em>
            </h2>
            <p className="cta-sub">
              14-day free trial. No credit card. Your first assessment fully guided.
            </p>
            <div className="cta-actions">
              <button className="btn-cta-primary" onClick={() => navigate('/assess')}>
                Start Your First Assessment
              </button>
              <button className="btn-cta-ghost" onClick={() => navigate('/login')}>
                Sign In to Dashboard
              </button>
            </div>
            <p className="cta-fine">14-day free trial · No credit card · Cancel any time</p>
          </div>
        </section>

        {/* ── FOOTER ──────────────────────────────────────────── */}
        <footer className="lp-footer" style={{ flexWrap: 'wrap' }}>
          <div className="footer-left">
            <span className="footer-wordmark">RCA Tool</span>
            <span className="footer-sep" />
            <span className="footer-copy">© {new Date().getFullYear()} Project governance, before the first budget is spent.</span>
          </div>
          <div className="footer-links">
            <a href="#assessment">The Assessment</a>
            <a href="#intelligence">The Intelligence</a>
            <a href="#pricing">Pricing</a>
            <a onClick={() => navigate('/login')}>Sign In</a>
            <a onClick={() => navigate('/privacy')}>Privacy</a>
          </div>
          <a
            href="https://morton-tech.com/services"
            style={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 300,
              fontSize: '9px',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'rgba(240,232,216,0.2)',
              textDecoration: 'none',
              width: '100%',
              textAlign: 'center',
              marginTop: '8px',
            }}
          >
            Powered by TheFirm
          </a>
        </footer>

      </div>
    </>
  )
}
